import { JURISDICTION_LIST } from '@/lib/constants';
import {
  TJurisdictionFee,
  useDeleteJurisdictionFee,
  useJurisdictionFees,
  useSaveJurisdictionFee,
} from '@/models/renewals';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useState } from 'react';
import { PiPencil, PiTrash } from 'react-icons/pi';

function FeeForm({
  opened,
  onClose,
  initial,
}: {
  opened: boolean;
  onClose: () => void;
  initial?: TJurisdictionFee;
}) {
  const [jurisdiction, setJurisdiction] = useState(initial?.jurisdiction ?? '');
  const [feeAmount, setFeeAmount] = useState<number | string>(
    initial?.feeAmount ?? 0,
  );
  const [feeCurrency, setFeeCurrency] = useState(initial?.feeCurrency ?? 'EUR');

  const saveMutation = useSaveJurisdictionFee();

  const handleSave = () => {
    if (!jurisdiction || !feeAmount) return;
    saveMutation.mutate(
      {
        jurisdiction,
        feeAmount: Number(feeAmount),
        feeCurrency,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={initial ? 'Edit Jurisdiction Fee' : 'Add Jurisdiction Fee'}
      size="sm"
    >
      <Stack gap="md">
        <Select
          label="Jurisdiction"
          searchable
          required
          data={JURISDICTION_LIST.map(({ code, title }) => ({
            value: code,
            label: title,
          }))}
          value={jurisdiction}
          onChange={(v) => setJurisdiction(v ?? '')}
          disabled={!!initial}
        />
        <Group grow>
          <NumberInput
            label="Fee Amount"
            required
            min={0}
            step={0.01}
            decimalScale={2}
            value={feeAmount}
            onChange={setFeeAmount}
          />
          <Select
            label="Currency"
            required
            data={['EUR', 'USD', 'GBP']}
            value={feeCurrency}
            onChange={(v) => setFeeCurrency(v ?? 'EUR')}
          />
        </Group>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saveMutation.isPending}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default function JurisdictionFeesPage() {
  const { data: fees = [], isLoading } = useJurisdictionFees();
  const deleteMutation = useDeleteJurisdictionFee();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TJurisdictionFee | undefined>();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const jurisdictionMap = new Map(
    JURISDICTION_LIST.map(({ code, title }) => [code, title]),
  );

  return (
    <Box p="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600} fz="lg">
            Jurisdiction Late Renewal Fees
          </Text>
          <Button
            size="sm"
            onClick={() => {
              setEditing(undefined);
              setFormOpen(true);
            }}
          >
            Add Fee
          </Button>
        </Group>

        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Jurisdiction</Table.Th>
              <Table.Th>Fee Amount</Table.Th>
              <Table.Th>Currency</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th w={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" fz="sm">
                    Loading...
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {!isLoading && fees.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" fz="sm">
                    No jurisdiction fees configured
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {fees.map((fee: TJurisdictionFee) => (
              <Table.Tr key={fee.id}>
                <Table.Td>
                  <Text fz="sm">
                    {jurisdictionMap.get(fee.jurisdiction) || fee.jurisdiction}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="sm">{Number(fee.feeAmount).toFixed(2)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="sm">{fee.feeCurrency}</Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="xs" c="dimmed">
                    {new Date(fee.updatedAt).toLocaleDateString('en-GB')}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => {
                        setEditing(fee);
                        setFormOpen(true);
                      }}
                    >
                      <PiPencil />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      color="red"
                      onClick={() => setDeleteConfirmId(fee.id)}
                    >
                      <PiTrash />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>

      <FeeForm
        key={editing?.id ?? 'new'}
        opened={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(undefined);
        }}
        initial={editing}
      />

      <Modal
        opened={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Jurisdiction Fee"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete this jurisdiction fee?
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => {
                if (deleteConfirmId) {
                  deleteMutation.mutate(deleteConfirmId, {
                    onSuccess: () => setDeleteConfirmId(null),
                  });
                }
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
